<script setup lang="ts">
import { VFloating, VIcon } from "@app/core";
import { useQuery } from "@vue/apollo-composable";
import { computed } from "vue";
import { useRouter } from "vue-router";

import { GET_ORGANIZATION } from "../api/organization.gql";
import { useSwitchOrganization } from "../composables/useSwitchOrganization";

import { RouteNames } from "@/app/router/types/names";
import { useAuthStore } from "@/features/auth";
import { useAppVersionQuery } from "@/shared/api/app";
import { useUserStore } from "@/shared/stores/useUserStore";

const { collapsed = false } = defineProps<{ collapsed?: boolean }>();

const router = useRouter();
const userStore = useUserStore();
const authStore = useAuthStore();

// ── Org data ──────────────────────────────────────────────────────────────────
// SuperAdmins switch orgs from the Organizations tab; this block only reflects
// the currently active organization.
const orgs = computed(() => userStore.user?.orgPermissions ?? []);
const activeOrgId = computed(() => userStore.user?.orgId ?? null);
const activeOrg = computed(
  () => orgs.value.find(o => o.orgId === activeOrgId.value) ?? orgs.value[0],
);
const activeOrgIndex = computed(() => {
  const idx = orgs.value.findIndex(o => o.orgId === activeOrgId.value);
  return idx >= 0 ? idx : 0;
});

// Authoritative active-org name: `orgPermissions` only covers orgs the user is a member
// of, but a SuperAdmin can switch into any org. Fetch the active org by id (backend allows
// ADMIN any org, others only their own) and fall back to the membership lookup on load/null.
const { result: activeOrgResult } = useQuery(
  GET_ORGANIZATION,
  () => ({ organizationId: activeOrgId.value ?? "" }),
  () => ({ enabled: !!activeOrgId.value }),
);
const activeOrgName = computed(
  () => activeOrgResult.value?.organization?.name ?? activeOrg.value?.orgName ?? "",
);

// ── Icon colour cycle (preserved from OrgSwitcher) ────────────────────────────
const ICON_CLASSES = [
  "from-primary-600 to-primary",
  "from-success-600 to-success",
  "from-warning-600 to-warning",
] as const;

const orgIconClass = (index: number): string =>
  ICON_CLASSES[index % ICON_CLASSES.length];

const orgInitials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join("")
    .toUpperCase() || "?";

// ── Org switching ───────────────────────────────────────────────────────────
// Any member of >1 org (incl. regular USERs) can switch their active org here. The
// backend's switchOrg authorises non-admins by active membership, and `orgs` only ever
// lists orgs the user actually belongs to — so they can never pick one they can't enter.
const { switchOrg, switching } = useSwitchOrganization();
const canSwitch = computed(() => orgs.value.length > 1);

const onSwitch = (orgId: string, close: () => void) => {
  close();
  switchOrg(orgId); // no-op when targeting the active org or mid-switch
};

// ── Handlers ──────────────────────────────────────────────────────────────────
const goToOrgPage = (close: () => void) => {
  close();
  router.push({ name: RouteNames.ORGANIZATION });
};

const goToProfile = (close: () => void) => {
  close();
  router.push({ name: RouteNames.MY_PROFILE });
};

const handleLogout = (close: () => void) => {
  close();
  authStore.logout();
};

// ── Versions ──────────────────────────────────────────────────────────────────

const { result: versionResult } = useAppVersionQuery();
const apiVersion = computed(() => versionResult.value?.appVersion ?? null);
const uiVersion = __UI_VERSION__;
</script>

<template>
  <div class="px-2 py-1.5">
    <VFloating
      class="!block"
      content-class="!p-0"
      placement="bottom-left"
    >
      <template #trigger>
        <button
          class="flex h-11 w-full items-center gap-2 rounded-lg border px-2
                 transition-colors duration-300 ease-in-out"
          :class="collapsed
            ? 'justify-center border-transparent hover:bg-base-300/50'
            : 'border-primary/20 bg-primary/10 hover:bg-primary/15 hover:border-primary/30'"
          type="button"
        >
          <!-- Org icon — always visible, fixed size to avoid layout jumps -->
          <div
            :class="orgIconClass(activeOrgIndex)"
            class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br
                   font-extrabold text-[10px] text-white"
          >
            {{ orgInitials(activeOrgName) }}
          </div>

          <!-- Org name + user line — fades out on collapse -->
          <Transition name="context-info-fade">
            <div
              v-if="!collapsed"
              class="flex min-w-0 flex-1 flex-col items-start leading-tight"
            >
              <span
                class="block max-w-[120px] overflow-hidden text-ellipsis
                       whitespace-nowrap text-xs font-semibold leading-tight text-mainText"
              >
                {{ activeOrgName }}
              </span>
              <span class="text-[10.5px] leading-tight text-secondaryText">
                {{ userStore.user?.name }} · {{ userStore.user?.role }}
              </span>
            </div>
          </Transition>

          <!-- User avatar — fades out on collapse -->
          <Transition name="context-info-fade">
            <div
              v-if="!collapsed && userStore.user"
              class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md
                     bg-gradient-to-br from-pink-500 to-purple-500
                     text-[9px] font-extrabold text-white"
            >
              {{ userStore.userInitials }}
            </div>
          </Transition>

          <!-- Chevron — fades out on collapse -->
          <Transition name="context-info-fade">
            <VIcon
              v-if="!collapsed"
              :size="14"
              class="flex-shrink-0 text-primary/50"
              icon="lucide:chevron-down"
            />
          </Transition>
        </button>
      </template>

      <template #content="{ close }">
        <!-- Org header — full row navigates to org page -->
        <button
          class="group flex w-full items-center gap-2.5 border-b border-base-300 px-3 py-2.5
                 text-left transition-colors duration-150 hover:bg-base-300/50
                 focus:outline-none focus-visible:outline-none"
          type="button"
          @click="goToOrgPage(close)"
        >
          <div
            :class="[orgIconClass(activeOrgIndex), 'h-8 w-8 rounded-lg text-[11px]']"
            class="flex flex-shrink-0 items-center justify-center bg-gradient-to-br
                   font-extrabold text-white"
          >
            {{ orgInitials(activeOrgName) }}
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold text-mainText">
              {{ activeOrgName }}
            </div>
            <div class="text-[11px] text-secondaryText">
              Organization
            </div>
          </div>
          <VIcon
            :size="14"
            class="ml-auto flex-shrink-0 text-secondaryText/40 transition-transform
                   duration-150 group-hover:translate-x-0.5 group-hover:text-secondaryText/70"
            icon="lucide:chevron-right"
          />
        </button>

        <!-- Org switcher — listed only when the user belongs to more than one org -->
        <div
          v-if="canSwitch"
          class="border-b border-base-300 py-1"
        >
          <div
            class="px-3 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-wide
                   text-secondaryText/60"
          >
            Switch organization
          </div>
          <button
            v-for="(o, i) in orgs"
            :key="o.orgId"
            class="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors
                   duration-150 hover:bg-base-300/50 focus:outline-none focus-visible:outline-none
                   disabled:cursor-default disabled:hover:bg-transparent"
            type="button"
            :disabled="o.orgId === activeOrgId || switching"
            @click="onSwitch(o.orgId, close)"
          >
            <div
              :class="[orgIconClass(i), 'h-7 w-7 rounded-md text-[10px]']"
              class="flex flex-shrink-0 items-center justify-center bg-gradient-to-br
                     font-extrabold text-white"
            >
              {{ orgInitials(o.orgName) }}
            </div>
            <span class="min-w-0 flex-1 truncate text-sm font-medium text-mainText">
              {{ o.orgName }}
            </span>
            <VIcon
              v-if="o.orgId === activeOrgId"
              :size="16"
              class="flex-shrink-0 text-primary"
              icon="lucide:check"
            />
          </button>
        </div>

        <!-- User section — full row navigates to My Profile -->
        <button
          class="group flex w-full items-center gap-2.5 px-3 py-2.5
                 text-left transition-colors duration-150 hover:bg-base-300/50
                 focus:outline-none focus-visible:outline-none"
          type="button"
          @click="goToProfile(close)"
        >
          <div
            class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md
                   bg-gradient-to-br from-pink-500 to-purple-500
                   text-[10px] font-extrabold text-white"
          >
            {{ userStore.userInitials }}
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold text-mainText">
              {{ userStore.user?.name }}
            </div>
            <div class="truncate text-xs text-secondaryText">
              {{ userStore.user?.email }}
            </div>
          </div>
          <VIcon
            :size="14"
            class="ml-auto flex-shrink-0 text-secondaryText/40 transition-transform
                   duration-150 group-hover:translate-x-0.5 group-hover:text-secondaryText/70"
            icon="lucide:chevron-right"
          />
        </button>

        <div class="py-0.5">
          <button
            class="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left
                   text-sm font-medium text-error transition-colors duration-150
                   hover:bg-error/10 focus:outline-none focus-visible:outline-none"
            type="button"
            @click="handleLogout(close)"
          >
            <VIcon
              :size="18"
              icon="mdi:logout"
            />
            Logout
          </button>
        </div>

        <div class="border-t border-base-300 px-3 py-2 text-center text-xs text-secondaryText/50">
          UI v{{ uiVersion }} · API v{{ apiVersion ?? "…" }}
        </div>
      </template>
    </VFloating>
  </div>
</template>

<style scoped>
/* Collapse: instant remove — no layout recalculation mid-animation */
.context-info-fade-leave-active { transition: none; }
.context-info-fade-leave-from   { opacity: 1; }
.context-info-fade-leave-to     { opacity: 0; }

/* Expand: fade in after sidebar finishes animating (300ms matches sidebar transition) */
.context-info-fade-enter-active { transition: opacity 0.15s ease 0.3s; }
.context-info-fade-enter-from   { opacity: 0; }
.context-info-fade-enter-to     { opacity: 1; }
</style>
